import {BasicBillableItem, BillableItems} from "./AccountBook";
import {Identifier, makeNumericId} from "../common/Identifier";
import {makeSensitive, Sensitive} from "../common/Sensitive";
import {Money, Percent} from "./Finance";
import {InvoiceItemType} from "./InvoiceItemType";
import {InvoiceItemUnitTypes} from "./InvoiceItemUnitTypes";
import {SoftDeletable} from "../common/SoftDeletable";

/**
 * Represents the unique identifier for an item within an outgoing invoice.
 *
 * This interface is used to encapsulate the identity of a specific invoice item,
 * enabling consistent referencing and tracking of items associated with outgoing invoices.
 *
 * @property outgoingInvoiceItemId - A unique identifier represented as an `Identifier<number>`.
 * It ensures that each outgoing invoice item is distinct and can be reliably referenced.
 */
export interface OutgoingInvoiceItemId {
    outgoingInvoiceItemId: Identifier< number >
}

/**
 * Represents an item included in an outgoing invoice, encapsulating both the billing details and associated identifiers.
 *
 * This interface is central to managing the items listed on outgoing invoices, allowing the system to handle
 * billing amounts, item-specific additional costs, and financial calculations such as percentage-based extra charges.
 * It ensures consistency in invoice item representation and supports extended properties through implemented interfaces.
 *
 * @extends BillableItems Includes properties and methods for handling base billable item data.
 * @extends OutgoingInvoiceItemId Provides unique identification for an invoice item.
 */
export interface OutgoingInvoiceItem extends BillableItems, OutgoingInvoiceItemId {
    additionalCosts: Sensitive<Money>
    additionalCostsPercent: Percent
}

/**
 * Represents the unique identifier for an invoice's discount or surcharge.
 *
 * This interface is used to associate a specific discount or surcharge entry with an invoice, ensuring traceability and accurate calculations during financial operations.
 *
 * @property id - A unique identifier provided as an `Identifier<number>` type, which ensures the integrity and uniqueness of the discount or surcharge within the system.
 */
export interface InvoiceDiscountSurchargeId {
    id: Identifier<number>
}

/**
 * Represents a discount or surcharge applied to an outgoing invoice.
 *
 * This interface is used to detail an adjustment to the invoice's total based on a specific reason,
 * percentage, or fixed amount, and whether the adjustment is a discount or surcharge.
 */
export interface OutgoingInvoiceDiscountSurcharge extends InvoiceDiscountSurchargeId {
    reason: string
    baseAmount: Sensitive<Money>
    percentage: Percent
    amount: Sensitive<Money>
    type: "discount" | "surcharge"
    invoiceId: Identifier<number>
    itemId: Identifier<number>
}


/**
 * Generates a default discount or surcharge configuration for an outgoing invoice.
 *
 * This function is used to initialize a discount or surcharge object with default values,
 * ensuring a consistent structure for invoices. It assigns default values for properties like
 * percentage, base amount, amount, and type.
 *
 * @param id - (Optional) The unique identifier for the discount or surcharge. Defaults to 0 if not provided.
 * @return An object representing the default configuration for an outgoing invoice discount or surcharge, including properties such as percentage, amount, type, and reason.
 */
export function defaultInvoiceDiscountSurcharge( id: number = 0): OutgoingInvoiceDiscountSurcharge {
    return {
        percentage: 0,
        baseAmount: makeSensitive(0),
        amount: makeSensitive(0),
        type: "discount",
        reason: "",
        invoiceId: makeNumericId(0),
        itemId: makeNumericId(0),
        id: makeNumericId(id)
    };
}

/**
 * Represents the discount or surcharge applied on an outgoing invoice item.
 * This facilitates tracking price adjustments specific to items within an invoice.
 *
 * Combines the unique identifiers and discount/surcharge details to provide
 * comprehensive information about adjustments to invoice item pricing.
 * Used in scenarios where line-item-level information is critical for pricing audits
 * or financial reporting.
 *
 * @extends OutgoingInvoiceItemId Provides the unique identifier of the outgoing invoice item.
 * @extends OutgoingInvoiceDiscountSurcharge Includes discount or surcharge details specific to the item.
 */
export interface OutgoingInvoiceItemDiscountSurcharge extends OutgoingInvoiceItemId, OutgoingInvoiceDiscountSurcharge, OutgoingInvoiceItemId {

}

/**
 * Generates a default invoice item discount/surcharge object.
 *
 * This method exists to automate the creation of a standardized discount/surcharge
 * object for outgoing invoice items. It ensures consistency in the discount/surcharge
 * structure while allowing customization through an optional identifier.
 *
 * The returned object is typically used in financial operations for invoice processing
 * to define an item-level adjustment (either a discount or surcharge).
 *
 * @param id - An optional numeric identifier for the discount/surcharge object. Defaults to `0`.
 * @return An instance of `OutgoingInvoiceItemDiscountSurcharge` pre-filled with default values,
 * including a generated numeric ID for the `outgoingInvoiceItemId` property.
 * @throws Throws an error if an internal dependency (e.g., `makeNumericId`) fails to generate a valid ID.
 */
export function defaultInvoiceITemDiscountSurcharge( id: number = 0): OutgoingInvoiceItemDiscountSurcharge {
    return {
        ...defaultInvoiceITemDiscountSurcharge( id ),
        outgoingInvoiceItemId: makeNumericId( 0 ),
    };
}

/**
 * Represents an item in a simple outgoing invoice, providing key details such as unit, quantity, pricing, and applicable discounts or surcharges.
 *
 * This interface is designed to model invoice items in systems that manage outgoing invoices, helping to structure item-specific data for billing processes.
 * Extends `OutgoingInvoiceItem` to include additional information on pricing and optional discount/surcharge adjustments.
 *
 * @extends OutgoingInvoiceItem
 *
 * @property unitType Specifies the unit of measurement for the item (e.g., pieces, kilograms), determined by `InvoiceItemUnitTypes`.
 * @property itemCount The total quantity of the item being invoiced. Must be a non-negative number.
 * @property singleItemPrice Represents the price for a single unit of the item, wrapped in `Sensitive<Money>` for secure handling of monetary values.
 * @property discountSurcharges An optional list of discounts or surcharges applied to the specific item. Each entry is defined by `OutgoingInvoiceDiscountSurcharge`.
 */
export interface SimpleOutgoingInvoiceItem extends OutgoingInvoiceItem, SoftDeletable {
    unitType: InvoiceItemUnitTypes
    itemCount: number
    singleItemPrice: Sensitive<Money>
    discountSurcharges?: OutgoingInvoiceDiscountSurcharge[]
}

/**
 * Type guard to check if a billable item is a SimpleOutgoingInvoiceItem.
 * @param obj The billable item to check.
 * @returns True if the item is a SimpleOutgoingInvoiceItem.
 */
export function isSimpleOutgoingInvoiceItem( obj: BasicBillableItem ): obj is SimpleOutgoingInvoiceItem {
    return obj.hasOwnProperty( "unitType" )
        && obj.hasOwnProperty( "itemCount" )
        && obj.hasOwnProperty( "singleItemPrice" )
        && obj.invoiceItemType == InvoiceItemType.BASIC
}

/**
 * Creates a default outgoing invoice item with pre-configured placeholder values.
 *
 * This function is used to initialize a new outgoing invoice item object with default values,
 * ensuring consistency and avoiding unintentional null or undefined state in financial data processing.
 *
 * The default values include zeroed numeric fields, an empty string for the name,
 * and basic invoice item types to provide a starting point for further configuration.
 * It ensures alignment with standard business logic, such as applying a flat rate unit,
 * default sales taxes, and base costs.
 *
 * Edge cases to consider:
 * - This function may not include all necessary fields when future expansions or changes
 *   to the `SimpleOutgoingInvoiceItem` structure occur.
 * - Sensitive fields are wrapped using the `makeSensitive` function for secure handling.
 *
 * @return {SimpleOutgoingInvoiceItem} A new instance of `SimpleOutgoingInvoiceItem` populated with default values.
 * @throws No exceptions are explicitly thrown by this function, but dependent utilities
 * like `makeNumericId` and `makeSensitive` must handle errors appropriately.
 */
export function defaultSimpleOutgoingInvoiceItem( id: number ): SimpleOutgoingInvoiceItem {
    return {
        name: "",
        invoiceId: makeNumericId(0),
        outgoingInvoiceItemId: makeNumericId(id),
        additionalCosts: makeSensitive(0.0),
        additionalCostsPercent: 0.0,
        itemCount: 0.0,
        singleItemPrice: makeSensitive(0.0),
        invoiceItemType: InvoiceItemType.BASIC,
        unitType: "flatrate",
        totals: {
            amountOwed: makeSensitive(0.00),
            amountOwedWithTax: makeSensitive(0.00),
            salesTax: { salesTax: 19.00, salesTaxAmount: makeSensitive(0.00) },
        },
        isDeleted: false,
    }
}
